import './season-form.scss';

import dayjs, { type Dayjs } from 'dayjs';
import { formatUpdatedAt, today } from 'features/dates/date-helpers';
import {
	selectCurrentSeason,
	unsavedDataChanged,
} from 'features/rules/redux/rules-slice';
import { Field, useFormikContext } from 'formik';
import { Select, TextField } from 'formik-mui';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { type SeasonFormValues, defaultValues } from './seasons';

import { CustomTooltip } from '@/components/custom-tooltip';
import { Label } from '@/components/ui/label';
import { MenuItem } from '@mui/material';
import { DayPicker } from 'components/DayPicker/DayPicker';
import { InputDate } from 'components/InputDate/InputDate';
import { useOnPropertyChange } from 'features/rules/seasons/seasons-hooks';
import { useUser } from 'features/users/context/userContext';
import { labels } from 'locales/en.label';
import type { SelectedDays } from 'types/SearchTypes';
import { SeasonPriority } from 'types/SeasonTypes';
import { RateChartMoved } from './rate-chart-moved';

// Helper function to get available priorities
const getAvailableUserPriorities = (includeLowest = false) => {
	return Object.entries(SeasonPriority)
		.filter(
			([_key, value]) =>
				!Number.isNaN(Number(value)) &&
				(includeLowest ? Number(value) >= 1 : Number(value) > 1),
		)
		.map(([key, value]) => ({ key, value: Number(value) }));
};

const getValidDays = (startDate: Dayjs, endDate: Dayjs) => {
	const dayNames: SelectedDays[] = [];
	let date: Dayjs = startDate;
	while (date.isSameOrBefore(endDate) && dayNames.length < 7) {
		const dayName = date.format('dddd').toLowerCase() as SelectedDays;
		if (!dayNames.includes(dayName)) {
			dayNames.push(dayName);
		}
		date = date.add(1, 'day');
	}
	return dayNames;
};

export const SeasonForm = () => {
	const { isReadOnly } = useUser();
	const dispatch = useAppDispatch();
	const selectedSeason = useAppSelector(selectCurrentSeason);
	const {
		values: { id, seasonName, startDate, endDate },
		dirty,
		setFieldValue,
		resetForm,
	} = useFormikContext<SeasonFormValues>();

	const [validDays, setValidDays] = useState<SelectedDays[]>([]);

	const defaultSelected = seasonName === labels.rules.default_season_name;

	useEffect(() => {
		dispatch(unsavedDataChanged(dirty));
	}, [dispatch, dirty]);

	const resetCallback = useCallback(() => {
		resetForm({ values: defaultValues });
	}, [resetForm]);
	useOnPropertyChange(resetCallback);

	useEffect(() => {
		if (selectedSeason) {
			const { season_start, season_end } = selectedSeason;
			setValidDays(getValidDays(dayjs(season_start), dayjs(season_end)));
		} else {
			setValidDays(getValidDays(dayjs(startDate), dayjs(endDate)));
		}
	}, [endDate, selectedSeason, startDate]);

	const priorityDropdown = (
		<div className='priority'>
			<Label htmlFor='priority'>{labels.add_form.priority}</Label>
			<Field
				name='priority'
				component={Select}
				disabled={defaultSelected || isReadOnly}
				inputProps={{
					id: 'priority',
				}}
			>
				{getAvailableUserPriorities(defaultSelected).map(({ key, value }) => (
					<MenuItem key={key} value={value}>
						{key}
					</MenuItem>
				))}
			</Field>
		</div>
	);

	return (
		<div className='season-form'>
			<div className='row'>
				<Field
					className='name'
					name='seasonName'
					label={labels.add_form.season_name}
					disabled={id !== null || isReadOnly}
					component={TextField}
				/>
			</div>
			<div className='row'>
				<InputDate
					name='startDate'
					label={labels.add_form.start_date}
					disablePast
					disabled={
						isReadOnly ||
						defaultSelected ||
						(id !== null && dayjs(startDate).isBefore(today()))
					}
					onValueChange={(val) => {
						const selectedDays = getValidDays(dayjs(val), dayjs(endDate));
						setFieldValue('selectedDays', selectedDays);
					}}
				/>
			</div>
			<div className='row'>
				<InputDate
					name='endDate'
					label={labels.add_form.end_date}
					disablePast
					disabled={defaultSelected || isReadOnly}
					minDate={dayjs(startDate)}
					onValueChange={(val) => {
						const selectedDays = getValidDays(dayjs(startDate), dayjs(val));
						setFieldValue('selectedDays', selectedDays);
					}}
				/>
				<div className='item'>
					<DayPicker
						name='selectedDays'
						validDays={validDays}
						isTooltipVisible
						isDisabled={defaultSelected || isReadOnly}
					/>
				</div>
			</div>
			<div className='row'>
				<Label className='highlight-myview-checkbox'>
					{labels.add_form.highlight_myview}
					<Field
						type='checkbox'
						name='highlightInMyView'
						disabled={defaultSelected || isReadOnly}
					/>
				</Label>
				<div className='item'>
					{(() => {
						return defaultSelected ? (
							<CustomTooltip
								content={
									<p className='tw-text-foreground'>
										Default season is required to be the Lowest and cannot be
										edited.
									</p>
								}
								backgroundColor='tw-bg-white'
								arrowColor='tw-fill-primary'
								arrow
								borderColor='tw-border-primary'
							>
								{priorityDropdown}
							</CustomTooltip>
						) : (
							priorityDropdown
						);
					})()}
				</div>
			</div>

			<RateChartMoved />

			<span className='updated-by'>
				{selectedSeason && (
					<>
						<span className='footnote'>* </span>
						{labels.add_form.last_updated_by} {selectedSeason.last_updated_by}{' '}
						{labels.add_form.on}{' '}
						{formatUpdatedAt(
							selectedSeason.last_updated_date,
							'M/D/YYYY h:mm A Z',
						)}
					</>
				)}
			</span>
		</div>
	);
};
